import OktaAuth, {
  AccessToken,
  IDToken,
  SessionObject,
  UserClaims,
} from '@okta/okta-auth-js';
import React, {createContext, useEffect, useMemo, useReducer} from 'react';
import {AuthLogin, UpdateState} from './AuthActions';
import AuthReducer, {AuthState, AuthProps} from './AuthReducer';
import authClient from './config';
import {
  handleAuthFlow,
  authLogout,
  isTokenValid,
  refreshSession,
  refreshToken,
} from './AuthHelpers';
import {APP_LOGOUT_URL} from '../config/getEnvironmentVars';

interface AuthContextTypes {
  accessToken?: AccessToken;
  authClient: OktaAuth;
  authLogin: (logoutURL?: string) => void;
  authLogout: () => void;
  idToken?: IDToken;
  initialState?: AuthProps;
  isAuthenticated: boolean;
  refreshAuthSession: () => void;
  refreshAuthToken: () => void;
  session?: SessionObject;
  user?: UserClaims;
}

export const AuthContext = createContext<AuthContextTypes>({
  accessToken: undefined,
  authClient,
  authLogin: () => {},
  authLogout: () => {},
  idToken: undefined,
  initialState: undefined,
  isAuthenticated: false,
  refreshAuthSession: () => {},
  refreshAuthToken: () => {},
  session: undefined,
  user: undefined,
});

const AuthProvider = ({children}) => {
  const [state, dispatch] = useReducer(AuthReducer, AuthState as AuthProps);

  const setAuthListeners = async () => {
    // Subscribe to authState change event.
    authClient.authStateManager.subscribe(async (authState) => {
      if (!authState.isAuthenticated) {
        if (!authClient.isLoginRedirect()) {
          handleAuthFlow(dispatch, state, authClient);
        }
        return;
      }

      // Verify Okta session exists before trusting local tokens.
      const session = await authClient.session.get();
      if (session.status !== 'ACTIVE') {
        authClient.tokenManager.clear();
        authClient.authStateManager.updateAuthState();
        return;
      }

      // Render authenticated view
      AuthLogin(dispatch, authState);
    });

    // Handle callback
    if (authClient.token.isLoginRedirect()) {
      await (async () => {
    const {state, tokens} = await authClient.token.parseFromUrl();
      authClient.tokenManager.setTokens(tokens);
      
      const decodedState = globalThis.atob(state);
      UpdateState(dispatch, decodedState);
      
      // Restore pathname and query params from OAuth state if they were saved
      try {
        const stateObj = JSON.parse(decodedState);
        const pathname = stateObj.pathname || '/';
        const { uuid, pathname: _, ...queryParams } = stateObj;
        const queryString = new URLSearchParams(queryParams).toString();
        const fullUrl = queryString ? `${pathname}?${queryString}` : pathname;
          globalThis.history.replaceState(null, '', fullUrl);
      } catch (e) {
        console.log('Failed to parse OAuth state:', e);
      }
      })();
    }

    // normal app startup
    authClient.start(); // will update auth state and call event listeners
  };

  useEffect(() => {
    setAuthListeners();
    return () => {
      authClient.stop();
    };
  }, []);

  useEffect(() => {
    if (state.user !== undefined) {
      isTokenValid(state, authClient, dispatch);
    }
  }, [state.user]);

  const authContextValues = useMemo(
    () => ({
      authClient,
      authLogin: () => handleAuthFlow(dispatch, state, authClient),
      authLogout: (logoutURL = APP_LOGOUT_URL) =>
        authLogout(dispatch, authClient, logoutURL),
      refreshAuthSession: () => refreshSession(dispatch, authClient),
      refreshAuthToken: () => refreshToken(authClient),
      ...state,
    }),
    [state],
  );

  return (
    <AuthContext.Provider value={authContextValues}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
